// mds-dojo — dojo/date/stamp: ISO-8601 parse/format, behavior-faithful port of
// dojo/date/stamp.js (BSD-3, © JS Foundation). Handles the app's uses:
// fromISOString("2026-07-17T10:00:00"), toISOString(d, {selector:'date'}), zulu.

export function fromISOString(formattedString: string, defaultTime?: number): Date | null {
  if (formattedString == null) { return null; }
  const isoRegExp = /^(?:(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?)?(?:T(\d{2}):(\d{2})(?::(\d{2})(\.\d+)?)?((?:[+-](\d{2}):(\d{2}))|Z)?)?$/;
  const match = isoRegExp.exec(formattedString);
  if (!match) { return null; }

  const m = match.slice(1) as any[];
  if (m[1]) { m[1]--; } // month is 0-based
  if (m[6]) { m[6] *= 1000; } // ms

  let result: Date;
  if (defaultTime != null) {
    const defaults = new Date(defaultTime);
    const dp = ['FullYear', 'Month', 'Date', 'Hours', 'Minutes', 'Seconds', 'Milliseconds']
      .map((prop) => (defaults as any)['get' + prop]());
    result = new Date(
      m[0] != null ? m[0] : dp[0],
      m[1] != null ? m[1] : dp[1],
      m[2] != null ? m[2] : dp[2],
      m[3] != null ? m[3] : dp[3],
      m[4] != null ? m[4] : dp[4],
      m[5] != null ? m[5] : dp[5],
      m[6] != null ? m[6] : dp[6],
    );
  } else {
    result = new Date(m[0] || 1970, m[1] || 0, m[2] || 1, m[3] || 0, m[4] || 0, m[5] || 0, m[6] || 0);
  }

  if (m[0] < 100) { result.setFullYear(m[0] || 1970); }

  let offset = 0;
  const zoneSign = m[7] && m[7].charAt(0);
  if (zoneSign !== 'Z' && m[7] != null) {
    offset = ((m[8] || 0) * 60) + (Number(m[9]) || 0);
    if (zoneSign !== '-') { offset *= -1; }
  }
  if (zoneSign) {
    offset -= result.getTimezoneOffset();
  }
  if (offset) {
    result.setTime(result.getTime() + offset * 60000);
  }
  return result;
}

export function toISOString(dateObject: Date, options?: { selector?: string; zulu?: boolean; milliseconds?: boolean }): string {
  const pad = (n: number, width = 2) => String(Math.abs(n)).padStart(width, '0');
  options = options || {};
  const formattedDate: string[] = [];
  const getter = (prop: string) => (options!.zulu ? (dateObject as any)['getUTC' + prop]() : (dateObject as any)['get' + prop]());

  if (options.selector !== 'time') {
    const year = getter('FullYear');
    formattedDate.push(
      (year > 9999 ? '+' : '') + pad(year, 4),
      pad(getter('Month') + 1),
      pad(getter('Date')),
    );
  }
  let formatted = formattedDate.join('-');

  if (options.selector !== 'date') {
    const time = [pad(getter('Hours')), pad(getter('Minutes')), pad(getter('Seconds'))].join(':')
      + (options.milliseconds ? '.' + pad(getter('Milliseconds'), 3) : '');
    formatted += (formatted ? 'T' : '') + time;
    if (options.zulu) {
      formatted += 'Z';
    } else if (options.selector !== 'time') {
      const timezoneOffset = dateObject.getTimezoneOffset();
      const absOffset = Math.abs(timezoneOffset);
      formatted += (timezoneOffset > 0 ? '-' : '+') + pad(Math.floor(absOffset / 60)) + ':' + pad(absOffset % 60);
    }
  }
  return formatted;
}

const stamp = { fromISOString, toISOString };
export default stamp;
